"use strict";
/**
 * Intended for testing suites, this module mocks the server logging API by
 * creating a real instance of pino, the server's library, that feeds all
 * entries to an array rather than to a file or stream.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.makeMockLog = makeMockLog;
exports.throwAtLevelOrWorse = throwAtLevelOrWorse;
exports.getLogsAtLevel = getLogsAtLevel;
exports.getLogsAtLevelOrWorse = getLogsAtLevelOrWorse;
exports.getLogsAtLevelOrBetter = getLogsAtLevelOrBetter;
exports.getLogsInLevelRange = getLogsInLevelRange;
const lodash_1 = __importDefault(require("lodash"));
const pino_1 = __importDefault(require("pino"));
/**
 * Create a mock logger. The return value is an object with these keys:
 * - pinoInst: The pino instance, equivalent to server.log or request.log in
 *   actual server code.
 * - entries: The array of logged entries, each a JSONable object. They mimic
 *   the usual entries but with some environment properties like pid removed.
 *   The array is mutated when pinoInst is used.
 * - listeners: Array of functions called for each logged entry. Best to leave
 *   to internal use, e.g. in throwAtLevelOrWorse.
 */
function makeMockLog() {
    const entries = [];
    const listeners = [];
    const logWriter = {
        write(str) {
            let obj = JSON.parse(str);
            // Ignore environment stuff that tests don't care about.
            obj = lodash_1.default.omit(obj, ["time", "pid", "hostname"]);
            listeners.forEach((f) => f(obj));
            entries.push(obj);
        },
    };
    const pinoInst = (0, pino_1.default)({
        level: "trace",
    }, logWriter);
    return {
        entries,
        listeners,
        pinoInst,
    };
}
/**
 * Registers a listener on a makeMockLog return. If any log entry has the
 * passed level or more severe, throw an error.
 *
 * This is used as a convenience to quickly fail a test if the code trips an
 * unexpected case that wouldn't normally throw.
 */
function throwAtLevelOrWorse(m, level) {
    const levelNum = pino_1.default.levels.values[level];
    if (levelNum === undefined) {
        throw new Error("Invalid level argument passed");
    }
    m.listeners.push((e) => {
        if (!e.level || e.level >= levelNum) {
            throw new Error("Severe log entry written: " + JSON.stringify(e));
        }
    });
}
/**
 * Small helper to get all log entries at exactly a certain level. Also
 * picks up entries with no level (which shouldn't happen).
 */
function getLogsAtLevel(m, level) {
    const levelNum = pino_1.default.levels.values[level];
    if (levelNum === undefined) {
        throw new Error("Invalid level argument passed");
    }
    return m.entries.filter((e) => !e.level || e.level === levelNum);
}
/**
 * Small helper to get all log entries at a certain level or those of more
 * severity. Also picks up entries with no level (which shouldn't happen).
 */
function getLogsAtLevelOrWorse(m, level) {
    const levelNum = pino_1.default.levels.values[level];
    if (levelNum === undefined) {
        throw new Error("Invalid level argument passed");
    }
    return m.entries.filter((e) => !e.level || e.level >= levelNum);
}
/**
 * Small helper to get all log entries at a certain level or those of less
 * severity. Also picks up entries with no level (which shouldn't happen).
 */
function getLogsAtLevelOrBetter(m, level) {
    const levelNum = pino_1.default.levels.values[level];
    if (levelNum === undefined) {
        throw new Error("Invalid level argument passed");
    }
    return m.entries.filter((e) => !e.level || e.level <= levelNum);
}
/**
 * Small helper to get all log entries in between two log levels inclusive. The
 * two levels can be passed in either order.
 */
function getLogsInLevelRange(m, lo, hi) {
    let loNum = pino_1.default.levels.values[lo];
    let hiNum = pino_1.default.levels.values[hi];
    if (loNum === undefined || hiNum === undefined) {
        throw new Error("Invalid level argument passed");
    }
    // Handle the other argument order.
    if (loNum > hiNum) {
        const swapTmp = loNum;
        loNum = hiNum;
        hiNum = swapTmp;
    }
    // For some reason Typescript thinks the swap could make those two undefined
    // again, so we'll ! things down here...
    return m.entries.filter((e) => !e.level || (e.level >= loNum && e.level <= hiNum));
}
//# sourceMappingURL=MockLog.js.map